import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './Sidebar';
import FounderBar from './FounderBar';
import LogoImg from './LogoImg';
import { useAuth } from '../context/AuthContext';
import { Coins, LayoutDashboard, BookOpen, Compass, Briefcase, MoreHorizontal } from './CustomIcons';

const BOTTOM_NAV = [
  { path: '/dashboard', label: 'Home',    icon: LayoutDashboard },
  { path: '/learn',     label: 'Learn',   icon: BookOpen },
  { path: '/jobs',      label: 'Jobs',    icon: Briefcase },
  { path: '/reentry',   label: 'Reentry', icon: Compass },
  { path: '/rewards',   label: 'More',    icon: MoreHorizontal },
];

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const profile = user?.profile || {};

  return (
    <div className="app-layout">
      {/* WCAG Skip Navigation Link */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* Mobile top header — only on <1024px */}
      <div className="mobile-header">
        <Link to="/dashboard" style={{ textDecoration: 'none' }}>
          <LogoImg height={28} alt="Assistiv OS" style={{ display: 'inline' }} />
        </Link>
        {user?.role === 'learner' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Coins size={14} style={{ color: '#F97316' }} aria-hidden="true" />
            <span style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800,
              fontSize: '15px', color: '#F97316',
            }} data-testid="mobile-credit-balance">
              {(profile.credits || 0).toLocaleString()}
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>cr</span>
          </div>
        )}
      </div>

      {/* Desktop sidebar */}
      <div className="sidebar-desktop">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      {/* Main content with page transition */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.main
          key={location.pathname}
          id="main-content"
          role="main"
          className="main-content md:pt-0 pt-14 pb-20 md:pb-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          tabIndex={-1}
          style={{ outline: 'none' }}
        >
          {/* FounderBar: sticky top bar visible to is_super_admin or during impersonation */}
          <FounderBar />
          {children}
        </motion.main>
      </AnimatePresence>

      {/* Mobile bottom nav — only on <1024px */}
      <nav className="bottom-nav" role="navigation" aria-label="Mobile navigation">
        {BOTTOM_NAV.map(({ path, label, icon: Icon }) => {
          const active = location.pathname === path || location.pathname.startsWith(path + '/');
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`bottom-nav-item${active ? ' active' : ''}`}
              aria-label={label}
              aria-current={active ? 'page' : undefined}
              data-testid={`bottom-nav-${label.toLowerCase()}`}
            >
              <Icon size={20} aria-hidden="true" />
              {label}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
